import { BlButton } from "Components/Shared/Buttons/BlButton";
import { YearSelectorMenu } from "Components/Settings/Invoices/YearSelectorMenu";
import { DownloadProgressModal } from "Components/Shared/DownloadProgressModal";
import { useAppDispatch } from "Hooks/State/useAppDispatch";
import { getAllUserReceivedInvoicePdfsAsync } from "State/Invoice/GetAllIUserReceivedInvoicePdfsSlice";
import { getUserReceivedInvoicesAvailableYearsAsync } from "State/Invoice/GetUserReceivedInvoicesAvailableYearsSlice";
import { useAppSelector } from "State/Store";
import { Resources, useResource } from "Translations/Resources";
import { useEffect, useState } from "react";

type Props = {
	isDisabled: boolean;
};

export const DownloadAllUserReceivedInvoicesButton: React.FunctionComponent<
	Props
> = props => {
	const { t } = useResource();
	const dispatch = useAppDispatch();
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

	const { isLoading: isDownloading } = useAppSelector(
		e => e.invoice.allUserReceivedInvoicesPdf,
	);

	const { data: yearsData, isLoading: isLoadingYears, error: yearsError } = useAppSelector(
		e => e.invoice.userReceivedInvoicesAvailableYears,
	);

	// Fetch available years on mount
	useEffect(() => {
		dispatch(getUserReceivedInvoicesAvailableYearsAsync.request());
	}, [dispatch]);

	const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		// If years are still loading or failed to load, do nothing (button is disabled)
		if (isLoadingYears || yearsError || !yearsData) {
			return;
		}

		const years = yearsData.years || [];

		// If no years available, do nothing
		if (years.length === 0) {
			return;
		}

		// If only 1 year, auto-download
		if (years.length === 1) {
			dispatch(getAllUserReceivedInvoicePdfsAsync.request(years[0]));
			return;
		}

		// If multiple years, show year selector menu
		setAnchorEl(event.currentTarget);
	};

	const handleYearSelected = (year: number) => {
		dispatch(getAllUserReceivedInvoicePdfsAsync.request(year));
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
	};

	return (
		<>
			<BlButton
				isLoading={isDownloading || isLoadingYears}
				disabled={props.isDisabled}
				onClick={handleButtonClick}
			>
				{t(Resources.Settings.Invoices.InvoicesList.DownloadAll.Button)}
			</BlButton>

			<YearSelectorMenu
				anchorEl={anchorEl}
				years={yearsData?.years || []}
				onYearSelected={handleYearSelected}
				onClose={handleMenuClose}
			/>

			<DownloadProgressModal
				open={isDownloading}
				title={t(Resources.Settings.Invoices.InvoicesList.DownloadAll.Progress.Title)}
				message={t(Resources.Settings.Invoices.InvoicesList.DownloadAll.Progress.Message)}
			/>
		</>
	);
};
